import { useState, useEffect, useCallback, useRef } from 'react';

const STORAGE_KEY = 'cyberForestHunt';
const VOUCHER_STORAGE_KEY = 'huntDiscount';
const COOKIE_DAYS_CLOSED = 7;      // Suppress 7 days if closed/skipped
const COOKIE_DAYS_COMPLETED = 30;  // Suppress 30 days if completed
const TIME_GATE_MS = 150000;       // 150 seconds (2.5 minutes) - auto-show after this

/**
 * Hook to manage Cyber Forest Hunt trigger logic
 * 
 * TRIGGER CONDITIONS (OR logic - either triggers popup):
 * 1. TIME ELAPSED: After 150 seconds on page → popup auto-shows
 * 2. EXIT INTENT: At ANY TIME, if user shows exit intent → popup shows immediately
 *    - mouseleave at top of viewport (clientY <= 0)
 *    - mouse in top zone (clientY <= 10)
 *    - visibilitychange to hidden (tab switch)
 * 
 * MOBILE: Disabled (exit intent unreliable on touch devices)
 * FREQUENCY: Max once per session
 * SUPPRESSION: 7 days if closed, 30 days if completed
 * EXCLUDED PAGES: checkout, payment, confirmation
 */
export const useCyberForestHuntTrigger = (options = {}) => {
  const { enabled = true } = options;
  
  const [shouldShow, setShouldShow] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [hasCompletedHunt, setHasCompletedHunt] = useState(false);
  
  // Refs to track state without causing re-renders
  const hasTriggeredThisSessionRef = useRef(false);
  const cleanupFunctionsRef = useRef([]);

  // Check if on excluded page (checkout, payment, confirmation)
  const isExcludedPage = useCallback(() => {
    const path = window.location.pathname.toLowerCase();
    return path.includes('checkout') || 
           path.includes('payment') || 
           path.includes('confirm') ||
           path.includes('success');
  }, []);

  // Check if mobile/tablet (exit intent unreliable)
  const isMobileOrTablet = useCallback(() => {
    return window.innerWidth <= 1024 || 
           'ontouchstart' in window || 
           navigator.maxTouchPoints > 0;
  }, []);

  // Check localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        if (data.completed) {
          setHasCompletedHunt(true);
        }
      }
    } catch (e) {
      // Storage not available
    }
  }, []);

  // Check if should suppress (seen recently or completed recently)
  const shouldSuppress = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        
        // Check if completed and within 30-day window
        if (data.completed && data.completedAt) {
          const completedExpiry = data.completedAt + (COOKIE_DAYS_COMPLETED * 24 * 60 * 60 * 1000);
          if (Date.now() < completedExpiry) {
            return true;
          }
        }
        
        // Check if closed/skipped and within 7-day window
        if (data.lastClosed) {
          const closedExpiry = data.lastClosed + (COOKIE_DAYS_CLOSED * 24 * 60 * 60 * 1000);
          if (Date.now() < closedExpiry) {
            return true;
          }
        }
      }
      
      // Check if voucher already claimed
      const voucherData = localStorage.getItem(VOUCHER_STORAGE_KEY);
      if (voucherData) {
        const voucher = JSON.parse(voucherData);
        if (voucher.claimed) {
          return true;
        }
      }
    } catch (e) {
      // Storage not available
    }
    return false;
  }, []);

  // Mark as closed (7-day suppression)
  const markAsClosed = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const data = stored ? JSON.parse(stored) : {};
      data.lastClosed = Date.now();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {}
  }, []);

  // Mark as completed (30-day suppression + save voucher)
  const markAsCompleted = useCallback((voucherCode = 'FOREST8') => {
    try {
      // Update hunt status
      const stored = localStorage.getItem(STORAGE_KEY);
      const data = stored ? JSON.parse(stored) : {};
      data.completed = true;
      data.completedAt = Date.now();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      setHasCompletedHunt(true);
      
      // Store voucher for checkout auto-apply (24 hour expiry)
      const voucherData = {
        code: voucherCode,
        percent: 8,
        expires: Date.now() + (24 * 60 * 60 * 1000),
        claimed: false,
        createdAt: Date.now()
      };
      localStorage.setItem(VOUCHER_STORAGE_KEY, JSON.stringify(voucherData));
      
      console.log('[CyberForestHunt] Hunt completed, voucher stored:', voucherCode);
    } catch (e) {
      console.error('[CyberForestHunt] Error storing completion:', e);
    }
  }, []);

  // Reset for testing (can be called from console: window.resetCyberForestHunt())
  useEffect(() => {
    window.resetCyberForestHunt = () => {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(VOUCHER_STORAGE_KEY);
      hasTriggeredThisSessionRef.current = false;
      setHasCompletedHunt(false);
      setShouldShow(false);
      setShowBanner(false);
      console.log('[CyberForestHunt] Storage cleared. Refresh to test popup.');
    };
    return () => {
      delete window.resetCyberForestHunt;
    };
  }, []);

  // Trigger popup function
  const triggerPopup = useCallback((reason) => {
    if (hasTriggeredThisSessionRef.current) return;
    if (isExcludedPage()) return;
    if (shouldSuppress()) return;
    
    hasTriggeredThisSessionRef.current = true;
    console.log(`[CyberForestHunt] Triggering popup - reason: ${reason}`);
    setShouldShow(true);
  }, [isExcludedPage, shouldSuppress]);

  // Main effect: Set up all triggers
  useEffect(() => {
    if (!enabled) return;
    
    // Skip on mobile/tablet
    if (isMobileOrTablet()) {
      console.log('[CyberForestHunt] Skipping on mobile/tablet');
      return;
    }
    
    // Skip on excluded pages
    if (isExcludedPage()) {
      console.log('[CyberForestHunt] Skipping on excluded page');
      return;
    }
    
    // Check suppression
    if (shouldSuppress()) {
      console.log('[CyberForestHunt] Suppressed (recently seen/completed/claimed)');
      return;
    }
    
    console.log('[CyberForestHunt] Initializing triggers...');
    console.log(`[CyberForestHunt] - Time trigger: ${TIME_GATE_MS/1000}s`);
    console.log('[CyberForestHunt] - Exit intent: ACTIVE (any time)');
    
    // ============================================
    // TRIGGER 1: TIME ELAPSED (150 seconds)
    // ============================================
    const timeTimer = setTimeout(() => {
      console.log('[CyberForestHunt] Time gate reached (150s)');
      triggerPopup('time_elapsed');
    }, TIME_GATE_MS);
    
    // ============================================
    // TRIGGER 2: EXIT INTENT (any time)
    // ============================================
    
    // 2a. Mouse leaving document at top (primary exit intent signal)
    const handleMouseLeave = (e) => {
      if (hasTriggeredThisSessionRef.current) return;
      if (e.clientY <= 0) {
        console.log('[CyberForestHunt] Exit intent: mouse left at top');
        triggerPopup('exit_intent_mouseleave');
      }
    };
    
    // 2b. Mouse entering top zone (backup - catches slower exits)
    let topZoneDebounce = null;
    const handleMouseMove = (e) => {
      if (hasTriggeredThisSessionRef.current) return;
      if (e.clientY <= 10) {
        // Debounce to avoid triggering on accidental brush
        if (!topZoneDebounce) {
          topZoneDebounce = setTimeout(() => {
            if (!hasTriggeredThisSessionRef.current && e.clientY <= 10) {
              console.log('[CyberForestHunt] Exit intent: mouse in top zone');
              triggerPopup('exit_intent_topzone');
            }
            topZoneDebounce = null;
          }, 300); // 300ms dwell time in top zone
        }
      } else {
        // Clear debounce if mouse moves away
        if (topZoneDebounce) {
          clearTimeout(topZoneDebounce);
          topZoneDebounce = null;
        }
      }
    };
    
    // 2c. Tab visibility change (user switching away)
    const handleVisibilityChange = () => {
      if (hasTriggeredThisSessionRef.current) return;
      if (document.visibilityState === 'hidden') {
        console.log('[CyberForestHunt] Exit intent: tab hidden');
        triggerPopup('exit_intent_visibility');
      }
    };
    
    // Attach all listeners immediately
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup
    return () => {
      clearTimeout(timeTimer);
      if (topZoneDebounce) clearTimeout(topZoneDebounce);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, isMobileOrTablet, isExcludedPage, shouldSuppress, triggerPopup]);

  // Close modal (mark as closed for 7-day suppression)
  const closeModal = useCallback(() => {
    setShouldShow(false);
    markAsClosed();
  }, [markAsClosed]);

  // Reopen modal (from footer link or banner)
  const reopenModal = useCallback(() => {
    setShouldShow(true);
    setShowBanner(false);
  }, []);

  // Close banner
  const closeBanner = useCallback(() => {
    setShowBanner(false);
  }, []);

  // CTA click handler (for tracking)
  const onCTAClick = useCallback(() => {
    console.log('[CyberForestHunt] User clicked CTA');
  }, []);

  return {
    shouldShow,
    showBanner,
    hasCompleted: hasCompletedHunt,
    closeModal,
    reopenModal,
    closeBanner,
    onCTAClick,
    markAsCompleted,
  };
};

export default useCyberForestHuntTrigger;
